import { db, auth } from './firebase';
import {
  collection, doc, getDoc, getDocs, addDoc, query, where, orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { ensureAuth } from './authInit';
import { apiGet, apiPost } from './apiClient';
import { getPackages, getPackageById } from './communityData';

function getUserHandle() {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) return 'web-visitor';
  return user.displayName || user.email?.split('@')[0] || 'web-visitor';
}

function getUserUid() {
  return auth.currentUser?.uid || null;
}

function isRealUser() {
  const user = auth.currentUser;
  return user && !user.isAnonymous;
}

// ── EndProducts (unified Community Hub) ─────────────────────────────────────

function isPublicEndProduct(product) {
  return (
    product?.securityReviewStatus === 'approved' &&
    product?.moderationStatus === 'clean' &&
    product?.removed !== true
  );
}

export async function fetchEndProducts(filters = {}) {
  try {
    // Try endProducts collection first (new unified model)
    const ref = collection(db, 'endProducts');
    const snapshot = await getDocs(ref);
    let products = snapshot.docs.map((d) => ({ ...d.data(), _id: d.id }));

    // Only show explicitly approved + clean + not soft-deleted products. Hub-soft-delete
    // (Phase HUB-SOFT 2026-05-17) flips `removed: true` on the endProducts doc
    // when the owner picks "Remove from Hub" on /account, OR when an admin
    // demotes it from /admin/hub. The URL /u/{uid}/{slug}/ still works (true
    // soft delete — only the public hub listing is hidden). Restore via the
    // same UI flips it back.
    products = products.filter(isPublicEndProduct);

    // Client-side filtering
    const queryStr = (filters.query ?? '').trim().toLowerCase();
    const category = (filters.category ?? '').trim().toLowerCase();

    if (queryStr) {
      products = products.filter((p) =>
        [p.name, p.summary, p.description, p.publisherName, ...(p.tags ?? [])]
          .join(' ').toLowerCase().includes(queryStr)
      );
    }
    if (category) {
      products = products.filter((p) => (p.contentCategory ?? p.type ?? '').toLowerCase() === category);
    }

    // Merge canonical official seed data for anything Firestore has not been
    // seeded with yet. Firestore docs still win when both exist.
    const firestoreIds = new Set(products.flatMap((p) => [p.id, p.packageId, p._id, p.slug, (p.name || '').toLowerCase()].filter(Boolean)));
    const seedProducts = getPackages({ query: queryStr, category }).filter(
      (s) => !firestoreIds.has(s.id) && !firestoreIds.has(s.packageId) && !firestoreIds.has(s.slug) && !firestoreIds.has((s.name || '').toLowerCase())
    );
    products = [...products, ...seedProducts];

    return products;
  } catch (err) {
    console.warn('[CommunityApi] endProducts fetch failed, using seed data:', err.message);
    return getPackages(filters);
  }
}

export async function fetchEndProduct(idOrSlug) {
  try {
    // Direct ID lookup
    const docRef = doc(db, 'endProducts', idOrSlug);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const product = { ...snap.data(), _id: snap.id };
      if (!isPublicEndProduct(product)) return null;
      product.comments = await fetchEndProductComments(product.id);
      return product;
    }

    // Slug lookup
    const q = query(collection(db, 'endProducts'), where('slug', '==', idOrSlug));
    const results = await getDocs(q);
    if (!results.empty) {
      const product = { ...results.docs[0].data(), _id: results.docs[0].id };
      if (!isPublicEndProduct(product)) return null;
      product.comments = await fetchEndProductComments(product.id);
      return product;
    }

    // Try seed data before legacy collection
    const seed = getPackageById(idOrSlug);
    if (seed) return { ...seed, comments: [] };

    return fetchLegacyPackage(idOrSlug);
  } catch (err) {
    console.warn('[CommunityApi] endProduct fetch failed:', err.message);
    const seed = getPackageById(idOrSlug);
    if (seed) return { ...seed, comments: [] };
    return fetchLegacyPackage(idOrSlug);
  }
}

// ── Comments ────────────────────────────────────────────────────────────────

async function fetchEndProductComments(productId) {
  try {
    const q = query(
      collection(db, 'endProductComments'),
      where('endProductId', '==', productId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      commentId: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.()?.toISOString() ?? new Date().toISOString()
    }));
  } catch {
    // Fall back to legacy comments collection
    return fetchLegacyComments(productId);
  }
}

export async function submitComment(productId, body) {
  // Route through API if signed in (server-side validation)
  if (isRealUser()) {
    try {
      const result = await apiPost(`/v1/community/packages/${productId}/comments`, { body });
      return { ok: true, comment: result.comment };
    } catch (err) {
      console.warn('[CommunityApi] API comment failed, falling back to Firestore:', err.message);
    }
  }
  // Fallback: direct Firestore write
  try {
    await ensureAuth();
    const handle = getUserHandle();
    const uid = getUserUid();
    const docRef = await addDoc(collection(db, 'endProductComments'), {
      endProductId: productId, authorHandle: handle, authorUid: uid, body, createdAt: serverTimestamp()
    });
    return { ok: true, comment: { commentId: docRef.id, authorHandle: handle, body, createdAt: new Date().toISOString() } };
  } catch {
    return { ok: true, comment: { commentId: `local-${Date.now()}`, authorHandle: getUserHandle(), body, createdAt: new Date().toISOString() } };
  }
}

// ── Ratings & Hearts ────────────────────────────────────────────────────────

export async function submitHeart(productId) {
  try {
    await ensureAuth();
    await addDoc(collection(db, 'endProductHearts'), {
      endProductId: productId, handle: getUserHandle(), uid: getUserUid(), createdAt: serverTimestamp()
    });
    return { ok: true };
  } catch {
    // Fall back to legacy
    try {
      await addDoc(collection(db, 'hearts'), { packageId: productId, handle: getUserHandle(), createdAt: serverTimestamp() });
    } catch { /* ignore */ }
    return { ok: true };
  }
}

export async function submitRating(productId, rating) {
  if (!isRealUser()) {
    return { ok: false, error: 'signInRequired' };
  }
  try {
    const resp = await apiPost(`/v1/community/packages/${productId}/ratings`, { rating });
    return { ok: true, yourRating: resp?.yourRating ?? rating };
  } catch (err) {
    console.warn('[CommunityApi] rating submit failed:', err.message);
    return { ok: false, error: err.message || 'submitFailed' };
  }
}

export async function fetchMyRating(productId) {
  if (!isRealUser()) return null;
  try {
    const resp = await apiGet(`/v1/community/packages/${productId}/ratings/mine`);
    return resp?.rating ?? null;
  } catch {
    return null;
  }
}

// ── Publishing (API-only) ───────────────────────────────────────────────────

export async function registerPublisher({ displayName, homepage, bio }) {
  return apiPost('/v1/publishers/register', { displayName, homepage, bio });
}

export async function publishPackage({ name, summary, description, type, tags, contentCategory }) {
  return apiPost('/v1/community/packages/publish', { name, summary, description, type, tags, contentCategory });
}

export async function getSignedUploadUrl(filename, contentType) {
  return apiPost('/v1/uploads/sign', { filename, contentType });
}

// ── Legacy Fallbacks (packages collection) ──────────────────────────────────

async function fetchLegacyPackage(idOrSlug) {
  try {
    const docRef = doc(db, 'packages', idOrSlug);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const pkg = { ...snap.data(), _id: snap.id };
      pkg.comments = await fetchLegacyComments(pkg.packageId || idOrSlug);
      return pkg;
    }
    const q = query(collection(db, 'packages'), where('slug', '==', idOrSlug));
    const results = await getDocs(q);
    if (!results.empty) {
      const pkg = { ...results.docs[0].data(), _id: results.docs[0].id };
      pkg.comments = await fetchLegacyComments(pkg.packageId || idOrSlug);
      return pkg;
    }
  } catch { /* fall through to seed */ }

  // Final fallback: seed data
  const seed = getPackageById(idOrSlug);
  return seed ? { ...seed, comments: [] } : null;
}

async function fetchLegacyComments(packageId) {
  try {
    const q = query(collection(db, 'comments'), where('packageId', '==', packageId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      commentId: d.id, ...d.data(),
      createdAt: d.data().createdAt?.toDate?.()?.toISOString() ?? new Date().toISOString()
    }));
  } catch {
    return [];
  }
}

// ── Compat exports (used by existing pages) ─────────────────────────────────

export const fetchCommunityPackages = fetchEndProducts;
export const fetchCommunityPackage = fetchEndProduct;
export async function fetchCommunityOverview() {
  const products = await fetchEndProducts({});
  return {
    ok: true,
    totals: {
      products: products.length,
      downloads: products.reduce((s, p) => s + (p.downloadCount ?? p.installs ?? 0), 0),
      ratings: products.reduce((s, p) => s + (p.ratingCount ?? 0), 0),
    }
  };
}
export async function prepareInstallIntent(productId) {
  const product = await fetchEndProduct(productId);
  if (!product) return null;
  return {
    delivery: {
      uri: `com.sevenhillsstudio.mchatai://community/install?id=${encodeURIComponent(product.id || product.packageId)}`
    }
  };
}
